import { useCallback } from "react";
import Cookies from "js-cookie";
import { useAuthRequestB2C } from "@danishagro/shared/src/contexts/basket/hooks/useAuthRequestB2C.hook";
import { Favorite } from "@interfaces/favorite.interface";

interface CookieFavorite {
    productId: string;
    variantId: string;
}

export const useFavoritesApi = () => {
    const authRequest = useAuthRequestB2C();
    const url = "/api/FavoriteList";

    const enrichCookieFavorites = useCallback(
        (list: CookieFavorite[]): Promise<Favorite[]> => {
            if (list?.length > 0) {
                return authRequest
                    .post(`${url}/enrich`, JSON.stringify(list))
                    .then((response) => response.json())
                    .then((data) => {
                        const result = data.favoriteList;
                        return result;
                    })
                    .catch((error) => {
                        console.error("Error enriching cookie favorites:", error);
                        return [];
                    });
            }
            return Promise.resolve([]);
        },
        [authRequest, url]
    );

    const fetchCookieFavorites = useCallback(async (): Promise<Favorite[]> => {
        const cookieFavorites: CookieFavorite[] = Cookies.getJSON("favorites") || [];

        if (cookieFavorites) {
            return enrichCookieFavorites(cookieFavorites);
        }

        return Promise.resolve([]);
    }, [enrichCookieFavorites]);

    const addCookieFavorite = useCallback(
        async (productId: string, variantId: string) => {
            const cookieFavorites: CookieFavorite[] = Cookies.getJSON("favorites") || [];
            cookieFavorites.push({ productId, variantId });
            Cookies.set("favorites", cookieFavorites);

            return await enrichCookieFavorites(cookieFavorites);
        },
        [enrichCookieFavorites]
    );

    const removeCookieFavorite = useCallback(
        async (productId: string, variantId: string) => {
            const cookieFavorites: CookieFavorite[] = Cookies.getJSON("favorites") || [];
            const filteredFavorites = cookieFavorites.filter(
                (favorite) => favorite.productId !== productId || favorite.variantId !== variantId
            );
            Cookies.set("favorites", filteredFavorites);

            return await enrichCookieFavorites(filteredFavorites);
        },
        [enrichCookieFavorites]
    );

    /*
    ------------------------------------------------------------
        Authenticated methods
    ------------------------------------------------------------
    */
    const fetchFavorites = useCallback(async () => {
        try {
            const response = await authRequest.get(url);
            const data = await response.json();
            return data.favoriteList;
        } catch (error) {
            console.error("Error fetching favorites:", error);
            return [];
        }
    }, [authRequest]);

    const addFavorite = useCallback(
        async (productId: string, variantId: string) => {
            const response = await authRequest.post(url, {
                productId,
                variantId: variantId || "",
            });
            const data = await response.json();
            return data.favoriteList;
        },
        [authRequest]
    );

    const removeFavorite = useCallback(
        async (productId: string, variantId: string) => {
            const response = await authRequest.delete(url, {
                productId,
                variantId: variantId || "",
            });
            const data = await response.json();
            return data.favoriteList;
        },
        [authRequest]
    );

    return {
        fetchFavorites,
        fetchCookieFavorites,
        addFavorite,
        addCookieFavorite,
        removeFavorite,
        removeCookieFavorite,
    };
};
